import React, { useState, useEffect } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';

const SECTION_CONFIG = {
  INCOME: { label: 'Income', icon: 'TrendingUp', color: 'text-success', bgColor: 'bg-success/10' },
  EXPENSE: { label: 'Expenses', icon: 'TrendingDown', color: 'text-error', bgColor: 'bg-error/10' },
  EXPENSES: { label: 'Expenses', icon: 'TrendingDown', color: 'text-error', bgColor: 'bg-error/10' },
  COGS: { label: 'Cost of Goods Sold', icon: 'ShoppingCart', color: 'text-warning', bgColor: 'bg-warning/10' },
  OTHER: { label: 'Other', icon: 'Layers', color: 'text-text-primary', bgColor: 'bg-surface-secondary' },
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const IncomeStatementPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const fetchIncomeStatement = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService?.get('/portal/financials/income-statement');
      const data = response?.data;
      if (data?.status_code === 200) {
        const grouped = {};
        (data?.details || [])?.forEach((item) => {
          const key = item?.type_code || 'OTHER';
          if (!grouped?.[key]) grouped[key] = [];
          grouped?.[key]?.push(item);
        });
        setSections(grouped);
      } else {
        setError(data?.status_description || 'Failed to load income statement');
      }
    } catch (err) {
      setError(err?.response?.data?.status_description || 'Failed to load income statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncomeStatement();
  }, []);

  const handleAccountClick = async (account) => {
    setSelectedAccount(account);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const response = await apiService?.get('/portal/financials/income-statement', { account_code: account?.account_code });
      const data = response?.data;
      if (data?.status_code === 200) {
        setDetailData(data?.details || []);
      } else {
        setDetailError(data?.status_description || 'Failed to load account details');
      }
    } catch (err) {
      setDetailError(err?.response?.data?.status_description || 'Failed to load account details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedAccount(null);
    setDetailData(null);
    setDetailError(null);
  };

  const getSectionTotal = (items) => {
    return items?.reduce((sum, item) => sum + Number(item?.amount || 0), 0);
  };

  const sectionOrder = ['INCOME', 'COGS', 'EXPENSE', 'EXPENSES'];
  const allSectionKeys = [...new Set([...sectionOrder, ...Object.keys(sections)])];

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          } mt-16 p-6`}
        >
          {/* Breadcrumb & Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Finance</span>
              <Icon name="ChevronRight" size={14} />
              <span>Reports</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Income Statement</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Income Statement</h1>
                <p className="text-text-secondary text-sm mt-1">View your organization's revenues, expenses, and net income</p>
              </div>
              <button
                onClick={fetchIncomeStatement}
                className="flex items-center space-x-2 border border-border text-text-secondary px-4 py-2 rounded-lg hover:bg-surface-secondary transition-colors text-sm"
              >
                <Icon name="RefreshCw" size={15} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-text-secondary">Loading income statement...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Icon name="AlertCircle" size={40} className="text-error mb-3" />
              <p className="text-text-secondary">{error}</p>
              <button onClick={fetchIncomeStatement} className="mt-4 text-primary text-sm hover:underline">Retry</button>
            </div>
          ) : Object.keys(sections)?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Icon name="FileText" size={40} className="text-text-secondary mb-3" />
              <p className="text-text-secondary">No income statement data available.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {allSectionKeys?.map((typeCode) => {
                const items = sections?.[typeCode];
                if (!items || items?.length === 0) return null;
                const config = SECTION_CONFIG?.[typeCode] || { label: typeCode, icon: 'Layers', color: 'text-text-primary', bgColor: 'bg-surface-secondary' };
                const total = getSectionTotal(items);

                return (
                  <div key={typeCode} className="bg-surface border border-border rounded-lg overflow-hidden">
                    {/* Section Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface-secondary/50">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config?.bgColor}`}>
                          <Icon name={config?.icon} size={16} className={config?.color} />
                        </div>
                        <h2 className="text-base font-semibold text-text-primary">{config?.label}</h2>
                        <span className="text-xs text-text-secondary bg-surface border border-border rounded-full px-2 py-0.5">
                          {items?.length} account{items?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-text-secondary mb-0.5">Section Total</p>
                        <p className={`text-sm font-semibold ${total < 0 ? 'text-error' : config?.color}`}>
                          {formatNumber(total)}
                        </p>
                      </div>
                    </div>
                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left px-5 py-3 text-text-secondary font-medium">Account Code</th>
                            <th className="text-left px-5 py-3 text-text-secondary font-medium">Account Name</th>
                            <th className="text-right px-5 py-3 text-text-secondary font-medium">Amount</th>
                            <th className="px-5 py-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items?.map((item, index) => (
                            <tr
                              key={item?.account_code || index}
                              className="border-b border-border last:border-0 hover:bg-surface-secondary/50 transition-colors cursor-pointer"
                              onClick={() => handleAccountClick(item)}
                            >
                              <td className="px-5 py-3 font-mono text-text-primary font-medium">{item?.account_code}</td>
                              <td className="px-5 py-3 text-text-primary">{item?.account_name}</td>
                              <td className="px-5 py-3 text-right font-medium">
                                <span className={Number(item?.amount) < 0 ? 'text-error' : 'text-success'}>
                                  {formatNumber(item?.amount)}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right">
                                <Icon name="ChevronRight" size={16} className="text-text-secondary" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-surface-secondary/60 border-t border-border">
                            <td colSpan={2} className="px-5 py-3 text-sm font-semibold text-text-primary">
                              Total {config?.label}
                            </td>
                            <td className="px-5 py-3 text-right font-semibold">
                              <span className={total < 0 ? 'text-error' : config?.color}>
                                {formatNumber(total)}
                              </span>
                            </td>
                            <td className="px-5 py-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Account Detail Drawer */}
      {selectedAccount && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40" onClick={handleCloseDetail}>
          <div
            className="bg-surface h-full w-full max-w-lg shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <p className="text-xs text-text-secondary font-mono mb-0.5">{selectedAccount?.account_code}</p>
                <h2 className="text-base font-semibold text-text-primary">{selectedAccount?.account_name}</h2>
                <span className="text-xs text-text-secondary">{selectedAccount?.type_code}</span>
              </div>
              <button
                onClick={handleCloseDetail}
                className="text-text-secondary hover:text-text-primary transition-colors p-1 rounded-lg hover:bg-surface-secondary"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Summary Card */}
            <div className="px-6 py-4 border-b border-border bg-surface-secondary/30">
              <div className="bg-surface border border-border rounded-lg p-4 text-center">
                <p className="text-xs text-text-secondary mb-1">Amount</p>
                <p className="text-lg font-bold text-text-primary">{formatNumber(selectedAccount?.amount)}</p>
              </div>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">Account Breakdown</h3>
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-text-secondary text-sm">Loading details...</span>
                </div>
              ) : detailError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Icon name="AlertCircle" size={28} className="text-error mb-2" />
                  <p className="text-text-secondary text-sm">{detailError}</p>
                </div>
              ) : detailData && detailData?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-text-secondary font-medium">Code</th>
                        <th className="text-left py-2 text-text-secondary font-medium">Name</th>
                        <th className="text-right py-2 text-text-secondary font-medium">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData?.map((item, index) => (
                        <tr key={item?.account_code || index} className="border-b border-border last:border-0">
                          <td className="py-3 font-mono text-text-primary text-xs">{item?.account_code}</td>
                          <td className="py-3 text-text-primary">{item?.account_name}</td>
                          <td className="py-3 text-right font-medium">
                            <span className={Number(item?.amount) < 0 ? 'text-error' : 'text-success'}>
                              {formatNumber(item?.amount)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Icon name="FileText" size={28} className="text-text-secondary mb-2" />
                  <p className="text-text-secondary text-sm">No breakdown data available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeStatementPage;
